import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { Bar, Pie } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import CloseIcon from "@mui/icons-material/Close";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { getTasksAPI } from "../../../Action/api";
import { getUserDataFromToken } from "../../../Utils/urlAuthHandler";
import CurrentWeekDashboardCard from "../../HumanResources/Planner/components/CurrentWeekDashboardCard";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

const MyTaskDashboard = () => {
  // State variables for task counts and priority distribution
  const [taskCounts, setTaskCounts] = useState({
    toDo: 0,
    inProgress: 0,
    review: 0,
    done: 0,
    overdue: 0,
  });
  const [todoTaskPriorities, setTodoTaskPriorities] = useState({
    high: 0,
    medium: 0,
    low: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allTasks, setAllTasks] = useState([]); // New state for all tasks
  const [openTaskTable, setOpenTaskTable] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [todoTasks, setTodoTasks] = useState([]); // New state for todo tasks
  const [openStatusTable, setOpenStatusTable] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [pointsData, setPointsData] = useState({
    positive: 0,
    negative: 0,
    neutral: 0,
    overall: 0,
    neutralCount: 0,
  });
  const [tasksByPoints, setTasksByPoints] = useState({
    positive: [],
    negative: [],
    neutral: [],
  });
  const [openPointsTable, setOpenPointsTable] = useState(false);
  const [selectedPointsCategory, setSelectedPointsCategory] = useState(null);

  const priorityColors = {
    "High Priority": "#f44336",
    "Medium Priority": "#ff9800",
    "Low Priority": "#4caf50",
  };

  const statusColors = {
    "To Do": "#9e9e9e",
    Progress: "#2196f3",
    Review: "#ff9800",
    Done: "#4caf50",
    Overdue: "#f44336",
  };

  // Fetch task counts and To Do task priorities from the API
  useEffect(() => {
    const fetchTaskData = async () => {
      try {
        setLoading(true);
        const { uId } = getUserDataFromToken();

        if (!uId) {
          throw new Error("User ID not found in token");
        }
        const userId = uId.toLowerCase(); // Convert to lowercase
        const response = await getTasksAPI(userId);
        console.log("Fetched tasks:", response);

        if (response.data && response.data.status && response.data.tasks) {
          const tasks = response.data.tasks;

          // Calculate task counts by status
          const toDoCount = tasks.filter(
            (task) => task.Status === "To Do"
          ).length;
          const inProgressCount = tasks.filter(
            (task) => task.Status === "Progress"
          ).length;
          const reviewCount = tasks.filter(
            (task) => task.Status === "Review"
          ).length;
          const doneCount = tasks.filter(
            (task) => task.Status === "Done"
          ).length;

          // Calculate overdue tasks - only count non-Done tasks
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const overdueCount = tasks.filter((task) => {
            const dueDate = new Date(task.DueDate);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate < today && task.Status !== "Done";
          }).length;

          // Update task counts state
          setTaskCounts({
            toDo: toDoCount,
            inProgress: inProgressCount,
            review: reviewCount,
            done: doneCount,
            overdue: overdueCount,
          });

          // Calculate To Do tasks priority distribution
          const todoTasks = tasks.filter((task) => task.Status === "To Do");
          setTodoTasks(todoTasks); // Store todo tasks for table view

          const highPriorityCount = todoTasks.filter((task) =>
            task.Priority.startsWith("High Priority")
          ).length;
          const mediumPriorityCount = todoTasks.filter((task) =>
            task.Priority.startsWith("Medium Priority")
          ).length;
          const lowPriorityCount = todoTasks.filter((task) =>
            task.Priority.startsWith("Low Priority")
          ).length;

          // Update priority counts state
          setTodoTaskPriorities({
            high: highPriorityCount,
            medium: mediumPriorityCount,
            low: lowPriorityCount,
          });

          // Store all tasks for other calculations
          setAllTasks(tasks);

          // Calculate Points data - separate On Going tasks (Points = 0) from completed tasks with points
          const positivePointsTasks = tasks.filter((task) => task.Points > 0);
          const negativePointsTasks = tasks.filter((task) => task.Points < 0);
          const ongoingTasks = tasks.filter((task) => task.Points === 0); // On Going tasks

          const positivePoints = positivePointsTasks.reduce(
            (sum, task) => sum + task.Points,
            0
          );
          const negativePoints = negativePointsTasks.reduce(
            (sum, task) => sum + task.Points,
            0
          );
          // Overall points only from tasks with points (exclude ongoing)
          const overallPoints = positivePoints + negativePoints;

          setPointsData({
            positive: positivePoints,
            negative: Math.abs(negativePoints),
            neutral: 0,
            overall: overallPoints,
            neutralCount: ongoingTasks.length,
          });

          setTasksByPoints({
            positive: positivePointsTasks,
            negative: negativePointsTasks,
            neutral: ongoingTasks,
          });
        }

        setError(null);
      } catch (err) {
        console.error("Error fetching task data:", err.message);
        setError(`Error loading task data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchTaskData();
  }, []);

  // Today's date
  const today = new Date();
  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  const formattedDate = today.toLocaleDateString("en-US", options);

  // Motivational quotes
  const motivationalQuotes = [
    "The secret to getting ahead is getting started.",
    "Don't watch the clock; do what it does. Keep going.",
    "The way to get started is to quit talking and begin doing.",
    "Success is not final, failure is not fatal: It is the courage to continue that counts.",
    "You don't have to be great to start, but you have to start to be great.",
    "Productivity is never an accident. It is always the result of a commitment to excellence.",
    "The only way to do great work is to love what you do.",
  ];

  // Pick a random quote for today
  const todaysQuote =
    motivationalQuotes[Math.floor(Math.random() * motivationalQuotes.length)];

  // Task completion stats
  const completionPercentage =
    Math.round(
      (taskCounts.done /
        (taskCounts.toDo +
          taskCounts.inProgress +
          taskCounts.review +
          taskCounts.done)) *
        100
    ) || 0;

  const handleBarChartClick = (event, elements) => {
    if (elements.length > 0) {
      const index = elements[0].index;
      const statuses = ["To Do", "Progress", "Review", "Done", "Overdue"];
      setSelectedStatus(statuses[index]);
      setOpenStatusTable(true);
    }
  };

  const handleCloseStatusTable = () => {
    setOpenStatusTable(false);
    setSelectedStatus(null);
  };

  const handleChartClick = (event, elements) => {
    if (elements.length > 0) {
      const index = elements[0].index;
      const priorities = ["High Priority", "Medium Priority", "Low Priority"];
      setSelectedPriority(priorities[index]);
      setOpenTaskTable(true);
    }
  };

  const handleCloseTable = () => {
    setOpenTaskTable(false);
    setSelectedPriority(null);
  };

  const handlePointsChartClick = (event, elements) => {
    if (elements.length > 0) {
      const index = elements[0].index;
      const categories = ["positive", "negative", "neutral"];
      setSelectedPointsCategory(categories[index]);
      setOpenPointsTable(true);
    }
  };

  const handleClosePointsTable = () => {
    setOpenPointsTable(false);
    setSelectedPointsCategory(null);
  };

  // Sample data for the bar chart
  const barChartData = {
    labels: ["To Do", "In Progress", "Review", "Done", "Overdue"],
    datasets: [
      {
        label: "Tasks",
        data: [
          taskCounts.toDo,
          taskCounts.inProgress,
          taskCounts.review,
          taskCounts.done,
          taskCounts.overdue,
        ],
        backgroundColor: [
          "#9e9e9e",
          "#2196f3",
          "#ff9800",
          "#4caf50",
          "#f44336",
        ],
        borderRadius: 6,
        borderWidth: 1,
        borderColor: [
          "rgba(158, 158, 158, 0.2)",
          "rgba(33, 150, 243, 0.2)",
          "rgba(255, 152, 0, 0.2)",
          "rgba(76, 175, 80, 0.2)",
          "rgba(244, 67, 54, 0.2)",
        ],
      },
    ],
  };

  // Update the bar chart options
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        align: "center",
        labels: {
          boxWidth: 15,
          padding: 15,
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0.7)",
        padding: 10,
        cornerRadius: 8,
        titleFont: {
          size: 14,
          weight: "bold",
        },
        bodyFont: {
          size: 13,
        },
      },
      datalabels: {
        anchor: "end",
        align: "top",
        formatter: (value) => (value > 0 ? value : ""),
        color: "#333",
        font: {
          weight: "bold",
          size: 12,
        },
        padding: 6,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          drawBorder: false,
          color: "rgba(0,0,0,0.05)",
        },
        ticks: {
          font: {
            size: 12,
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
          },
        },
      },
    },
    layout: {
      padding: 10,
    },
    onClick: handleBarChartClick,
  };

  // Data for the pie chart - now showing priority distribution for To Do tasks only
  const pieChartData = {
    labels: [
      "High Priority Tasks",
      "Medium Priority Tasks",
      "Low Priority Tasks",
    ],
    datasets: [
      {
        data: [
          todoTaskPriorities.high,
          todoTaskPriorities.medium,
          todoTaskPriorities.low,
        ],
        backgroundColor: ["#f44336", "#ff9800", "#4caf50"],
        borderColor: [
          "rgba(244, 67, 54, 0.2)",
          "rgba(255, 152, 0, 0.2)",
          "rgba(76, 175, 80, 0.2)",
        ],
        borderWidth: 1,
        hoverOffset: 12,
      },
    ],
  };

  // Update the pie chart options
  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: handleChartClick,
    plugins: {
      legend: {
        position: "bottom",
        align: "center",
        labels: {
          boxWidth: 15,
          padding: 15,
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0.7)",
        padding: 10,
        cornerRadius: 8,
      },
      datalabels: {
        formatter: (value) => (value > 0 ? value : ""),
        color: "#fff",
        font: {
          weight: "bold",
          size: 14,
        },
        padding: 6,
      },
    },
    layout: {
      padding: 10,
    },
  };

  return (
    <Box
      sx={{
        padding: { xs: 2, md: 4 },
        backgroundColor: "#f9f9f9",
        minHeight: "100vh",
      }}
    >
      {/* <Paper
        elevation={0}
        sx={{
          padding: { xs: 2, md: 3 },
          mb: 3,
          borderRadius: 3,
          background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
          color: "white",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            opacity: 0.1,
            background:
              'url(\'data:image/svg+xml,%3Csvg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z" fill="%23ffffff" fill-opacity="1" fill-rule="evenodd"/%3E%3C/svg%3E\')',
            backgroundSize: "80px 80px",
            zIndex: 0,
          }}
        />

         <Box sx={{ zIndex: 1, mb: { xs: 2, sm: 0 } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              textShadow: "0px 2px 4px rgba(0,0,0,0.2)",
              fontSize: { xs: "1.5rem", md: "2rem" },
            }}
          >
            My Task Dashboard
          </Typography>
          <Typography variant="body1" sx={{ mt: 0.5, opacity: 0.9 }}>
            {formattedDate}
          </Typography>
        </Box> 

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", sm: "flex-end" },
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {completionPercentage}%
            </Typography>
            <Typography variant="body1">Tasks Completed</Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              maxWidth: "320px",
              textAlign: { xs: "center", sm: "right" },
            }}
          >
            {taskCounts.done} of{" "}
            {taskCounts.toDo +
              taskCounts.inProgress +
              taskCounts.review +
              taskCounts.done}{" "}
            tasks completed
          </Typography>
        </Box>
      </Paper> */}

      {/* Motivational Quote */}
      {/* <Paper
        elevation={1}
        sx={{
          padding: { xs: 2, md: 3 },
          mb: 3,
          borderRadius: 3,
          borderLeft: "6px solid #4caf50",
          backgroundColor: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              backgroundColor: "rgba(76, 175, 80, 0.1)",
              color: "#4caf50",
              mr: 2,
              flexShrink: 0,
            }}
          >
            <Typography variant="h5" component="span" sx={{ fontWeight: 700 }}>
              "
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontStyle: "italic",
              color: "#424242",
              fontWeight: 500,
              fontSize: { xs: "0.9rem", md: "1rem" },
            }}
          >
            {todaysQuote}
          </Typography>
        </Box>
      </Paper> */}

      {/* This Week's Plan (from the Planner module) */}
      <CurrentWeekDashboardCard />

      <Grid container spacing={3}>
        {/* Bar Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              padding: 3,
              borderRadius: 3,
              backgroundColor: "#ffffff",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
              height: "100%",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 3,
                color: "#333333",
                textAlign: "center",
              }}
            >
              Task Status Overview
            </Typography>
            <Box
              sx={{
                height: 300,
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {taskCounts.toDo === 0 &&
              taskCounts.inProgress === 0 &&
              taskCounts.review === 0 &&
              taskCounts.overdue === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    p: 2,
                    maxWidth: "80%",
                    animation: "fadeIn 0.5s ease-in-out",
                    "@keyframes fadeIn": {
                      "0%": { opacity: 0, transform: "translateY(10px)" },
                      "100%": { opacity: 1, transform: "translateY(0)" },
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      backgroundColor: "rgba(33, 150, 243, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 20px",
                    }}
                  >
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20 3H4C2.9 3 2 3.9 2 5V19C2 20.1 2.9 21 4 21H20C21.1 21 22 20.1 22 19V5C22 3.9 21.1 3 20 3ZM20 19H4V5H20V19Z"
                        fill="#2196F3"
                      />
                      <path
                        d="M19 17H5V15H19V17ZM19 13H5V11H19V13ZM19 9H5V7H19V9Z"
                        fill="#2196F3"
                      />
                    </svg>
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#2196F3", mb: 1 }}
                  >
                    No Task Data Available
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    There are currently no tasks in your workspace. When tasks
                    are assigned, you'll see a chart showing their distribution
                    by status here.
                  </Typography>
                  <Box
                    sx={{
                      p: 1,
                      backgroundColor: "rgba(33, 150, 243, 0.05)",
                      borderRadius: 2,
                      border: "1px dashed rgba(33, 150, 243, 0.3)",
                      display: "inline-block",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#1976D2" }}>
                      Tasks will be shown here when assigned by your manager
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Bar data={barChartData} options={barChartOptions} />
              )}
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2, textAlign: "center" }}
            >
              {taskCounts.toDo === 0 &&
              taskCounts.inProgress === 0 &&
              taskCounts.review === 0 &&
              taskCounts.overdue === 0
                ? "Your task overview will appear here once tasks are assigned"
                : "Stay focused on completing your pending tasks to increase productivity."}
            </Typography>
          </Paper>
        </Grid>

        {/* Pie Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              padding: 3,
              borderRadius: 3,
              backgroundColor: "#ffffff",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
              height: "100%",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 3,
                color: "#333333",
                textAlign: "center",
              }}
            >
              Task Priority Distribution (To Do List)
            </Typography>
            <Box
              sx={{
                height: 300,
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {todoTaskPriorities.high === 0 &&
              todoTaskPriorities.medium === 0 &&
              todoTaskPriorities.low === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    p: 2,
                    maxWidth: "80%",
                    animation: "fadeIn 0.5s ease-in-out",
                    "@keyframes fadeIn": {
                      "0%": { opacity: 0, transform: "translateY(10px)" },
                      "100%": { opacity: 1, transform: "translateY(0)" },
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      backgroundColor: "rgba(33, 150, 243, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 20px",
                    }}
                  >
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19Z"
                        fill="#2196F3"
                      />
                      <path
                        d="M12 17H14V11H12V17ZM12 10H14V8H12V10Z"
                        fill="#2196F3"
                      />
                    </svg>
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#2196F3", mb: 1 }}
                  >
                    No To Do Tasks Found
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Your To Do list is empty. When you add tasks with
                    priorities, they'll appear here in a nice chart.
                  </Typography>
                  <Box
                    sx={{
                      p: 1,
                      backgroundColor: "rgba(33, 150, 243, 0.05)",
                      borderRadius: 2,
                      border: "1px dashed rgba(33, 150, 243, 0.3)",
                      display: "inline-block",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#1976D2" }}>
                      Add tasks to see priority distribution
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Pie data={pieChartData} options={pieChartOptions} />
              )}
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2, textAlign: "center" }}
            >
              {todoTaskPriorities.high === 0 &&
              todoTaskPriorities.medium === 0 &&
              todoTaskPriorities.low === 0
                ? "Start creating tasks to track priority distribution"
                : "Focus on high priority tasks first to maximize your impact."}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Points Tracking Section - Only show if there are tasks with points */}
      {(pointsData.positive > 0 || pointsData.negative > 0) && (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={8}>
            <Paper
              elevation={2}
              sx={{
                padding: 3,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
                height: "100%",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: "#333333",
                  textAlign: "center",
                }}
              >
                Task Points Overview
              </Typography>

              {/* Visual Cards Display */}
              <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                {/* Positive Points Card */}
                <Box
                  onClick={(e) => handlePointsChartClick(e, [{ index: 0 }])}
                  sx={{
                    flex: 1,
                    p: 2.5,
                    borderRadius: 3,
                    background:
                      "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)",
                    color: "white",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 20px rgba(76, 175, 80, 0.4)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ opacity: 0.9, fontWeight: 500 }}
                    >
                      Positive Points
                    </Typography>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        backgroundColor: "rgba(255,255,255,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: "1.2rem" }}>✓</Typography>
                    </Box>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                    +{pointsData.positive}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    {tasksByPoints.positive.length}{" "}
                    {tasksByPoints.positive.length === 1 ? "task" : "tasks"}
                  </Typography>
                </Box>

                {/* Negative Points Card */}
                <Box
                  onClick={(e) => handlePointsChartClick(e, [{ index: 1 }])}
                  sx={{
                    flex: 1,
                    p: 2.5,
                    borderRadius: 3,
                    background:
                      "linear-gradient(135deg, #f44336 0%, #e57373 100%)",
                    color: "white",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 20px rgba(244, 67, 54, 0.4)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ opacity: 0.9, fontWeight: 500 }}
                    >
                      Negative Points
                    </Typography>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        backgroundColor: "rgba(255,255,255,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: "1.2rem" }}>⚠</Typography>
                    </Box>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                    -{pointsData.negative}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    {tasksByPoints.negative.length}{" "}
                    {tasksByPoints.negative.length === 1 ? "task" : "tasks"}
                  </Typography>
                </Box>
              </Box>

              {/* Bar Chart for Comparison */}
              <Box sx={{ height: 200, position: "relative" }}>
                <Bar
                  data={{
                    labels: ["Positive", "Negative"],
                    datasets: [
                      {
                        label: "Points",
                        data: [pointsData.positive, pointsData.negative],
                        backgroundColor: [
                          "rgba(76, 175, 80, 0.8)",
                          "rgba(244, 67, 54, 0.8)",
                        ],
                        borderRadius: 8,
                        borderWidth: 0,
                        barThickness: 60,
                      },
                    ],
                  }}
                  options={{
                    indexAxis: "y",
                    responsive: true,
                    maintainAspectRatio: false,
                    onClick: handlePointsChartClick,
                    plugins: {
                      legend: {
                        display: false,
                      },
                      title: {
                        display: false,
                      },
                      tooltip: {
                        enabled: false,
                      },
                      datalabels: {
                        anchor: "end",
                        align: "end",
                        formatter: (value, context) => {
                          const taskCount =
                            context.dataIndex === 0
                              ? tasksByPoints.positive.length
                              : tasksByPoints.negative.length;
                          return `${value} pts | ${taskCount} ${
                            taskCount === 1 ? "task" : "tasks"
                          }`;
                        },
                        color: "#333",
                        font: {
                          weight: "bold",
                          size: 13,
                        },
                        padding: 8,
                      },
                    },
                    scales: {
                      x: {
                        beginAtZero: true,
                        display: false,
                      },
                      y: {
                        display: true,
                        grid: {
                          display: false,
                        },
                        ticks: {
                          font: {
                            size: 13,
                            weight: 600,
                          },
                          color: "#666",
                        },
                      },
                    },
                    layout: {
                      padding: {
                        right: 100,
                      },
                    },
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, textAlign: "center" }}
              >
                Click on cards or bars to see detailed task breakdown
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={2}
              sx={{
                padding: 3,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: "#333333",
                  textAlign: "center",
                }}
              >
                Points Summary
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "rgba(76, 175, 80, 0.1)",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, color: "#4caf50" }}
                    >
                      Positive Points
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#66bb6a" }}>
                      ({tasksByPoints.positive.length}{" "}
                      {tasksByPoints.positive.length === 1 ? "task" : "tasks"})
                    </Typography>
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#4caf50" }}
                  >
                    +{pointsData.positive}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "rgba(244, 67, 54, 0.1)",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, color: "#f44336" }}
                    >
                      Negative Points
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#e57373" }}>
                      ({tasksByPoints.negative.length}{" "}
                      {tasksByPoints.negative.length === 1 ? "task" : "tasks"})
                    </Typography>
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#f44336" }}
                  >
                    -{pointsData.negative}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    borderRadius: 2,
                    backgroundColor:
                      pointsData.overall >= 0
                        ? "rgba(33, 150, 243, 0.1)"
                        : "rgba(255, 152, 0, 0.1)",
                    border: "2px solid",
                    borderColor:
                      pointsData.overall >= 0 ? "#2196f3" : "#ff9800",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: pointsData.overall >= 0 ? "#2196f3" : "#ff9800",
                    }}
                  >
                    Overall Points
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: pointsData.overall >= 0 ? "#2196f3" : "#ff9800",
                    }}
                  >
                    {pointsData.overall >= 0 ? "+" : ""}
                    {pointsData.overall}
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", fontStyle: "italic" }}
              >
                {pointsData.overall > 0
                  ? "Great job! Keep up the good work!"
                  : pointsData.overall < 0
                  ? "Focus on completing tasks to improve your score"
                  : "Complete more tasks to maintain your score"}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* On Going Tasks Section - Separate view for tasks with 0 points */}
      {pointsData.neutralCount > 0 && (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Paper
              elevation={2}
              sx={{
                padding: 3,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#333333",
                    }}
                  >
                    On Going Tasks
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tasks that haven't been scored yet (Points = 0)
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    backgroundColor: "rgba(158, 158, 158, 0.1)",
                    border: "2px solid #9e9e9e",
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: "#9e9e9e" }}
                  >
                    {pointsData.neutralCount}
                  </Typography>
                </Box>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Task ID</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Deadline</TableCell>
                      <TableCell>Task Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tasksByPoints.neutral.slice(0, 5).map((task) => (
                      <TableRow key={task.TaskID}>
                        <TableCell>{task.TaskID}</TableCell>
                        <TableCell>{task.Title}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "inline-block",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              backgroundColor:
                                task.Status === "Done"
                                  ? "rgba(76, 175, 80, 0.1)"
                                  : task.Status === "Progress"
                                  ? "rgba(33, 150, 243, 0.1)"
                                  : task.Status === "Review"
                                  ? "rgba(255, 152, 0, 0.1)"
                                  : "rgba(158, 158, 158, 0.1)",
                              color:
                                task.Status === "Done"
                                  ? "#4caf50"
                                  : task.Status === "Progress"
                                  ? "#2196f3"
                                  : task.Status === "Review"
                                  ? "#ff9800"
                                  : "#9e9e9e",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                            }}
                          >
                            {task.Status}
                          </Box>
                        </TableCell>
                        <TableCell>{task.Priority}</TableCell>
                        <TableCell>
                          {new Date(task.DueDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{task.TaskType}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {tasksByPoints.neutral.length > 5 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2, textAlign: "center", fontStyle: "italic" }}
                >
                  Showing 5 of {tasksByPoints.neutral.length} on going tasks
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Task Insights */}
      <Paper
        elevation={2}
        sx={{
          padding: 3,
          borderRadius: 3,
          backgroundColor: "#ffffff",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          mt: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 2,
            color: "#333333",
          }}
        >
          Today's Task Insights
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              sx={{
                p: 2,
                backgroundColor: "rgba(158, 158, 158, 0.05)",
                borderRadius: 2,
                border: "1px solid rgba(158, 158, 158, 0.1)",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: "#9e9e9e", fontWeight: 600, mb: 1 }}
              >
                To Do
              </Typography>
              <Typography variant="h4" sx={{ color: "#333" }}>
                {taskCounts.toDo}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              sx={{
                p: 2,
                backgroundColor: "rgba(33, 150, 243, 0.05)",
                borderRadius: 2,
                border: "1px solid rgba(33, 150, 243, 0.1)",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: "#2196f3", fontWeight: 600, mb: 1 }}
              >
                In Progress
              </Typography>
              <Typography variant="h4" sx={{ color: "#333" }}>
                {taskCounts.inProgress}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              sx={{
                p: 2,
                backgroundColor: "rgba(255, 152, 0, 0.05)",
                borderRadius: 2,
                border: "1px solid rgba(255, 152, 0, 0.1)",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: "#ff9800", fontWeight: 600, mb: 1 }}
              >
                Review
              </Typography>
              <Typography variant="h4" sx={{ color: "#333" }}>
                {taskCounts.review}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              sx={{
                p: 2,
                backgroundColor: "rgba(244, 67, 54, 0.05)",
                borderRadius: 2,
                border: "1px solid rgba(244, 67, 54, 0.1)",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: "#f44336", fontWeight: 600, mb: 1 }}
              >
                Overdue
              </Typography>
              <Typography variant="h4" sx={{ color: "#333" }}>
                {taskCounts.overdue}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            Keep pushing forward! Focus on your active tasks to meet your goals.
          </Typography>
        </Box>
      </Paper>

      {/* Task Table Dialog */}
      <Dialog
        open={openTaskTable}
        onClose={handleCloseTable}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: selectedPriority
              ? priorityColors[selectedPriority]
              : "#228B22",
            color: "white",
          }}
        >
          {selectedPriority} Tasks
          <IconButton
            aria-label="close"
            onClick={handleCloseTable}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TableContainer sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Task ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Deadline</TableCell>
                  <TableCell>Total Man Days</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {todoTasks
                  .filter((task) => task.Priority.startsWith(selectedPriority))
                  .map((task) => (
                    <TableRow key={task.TaskID}>
                      <TableCell>{task.TaskID}</TableCell>
                      <TableCell>{task.Title}</TableCell>
                      <TableCell>{task.Description}</TableCell>
                      <TableCell>
                        {new Date(task.DueDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{task.TotalManDays}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openStatusTable}
        onClose={handleCloseStatusTable}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: selectedStatus ? statusColors[selectedStatus] : "#228B22",
            color: "white",
          }}
        >
          {selectedStatus} Tasks
          <IconButton
            aria-label="close"
            onClick={handleCloseStatusTable}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TableContainer sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Task ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Deadline</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Total Man Days</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allTasks
                  .filter((task) => {
                    if (selectedStatus === "Overdue") {
                      const dueDate = new Date(task.DueDate);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      dueDate.setHours(0, 0, 0, 0);
                      return dueDate < today && task.Status !== "Done";
                    }
                    return task.Status === selectedStatus;
                  })
                  .map((task) => (
                    <TableRow key={task.TaskID}>
                      <TableCell>{task.TaskID}</TableCell>
                      <TableCell>{task.Title}</TableCell>
                      <TableCell>{task.Description}</TableCell>
                      <TableCell>
                        {new Date(task.DueDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{task.Priority}</TableCell>
                      <TableCell>{task.TotalManDays}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>

      {/* Points Table Dialog */}
      <Dialog
        open={openPointsTable}
        onClose={handleClosePointsTable}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor:
              selectedPointsCategory === "positive"
                ? "#4caf50"
                : selectedPointsCategory === "negative"
                ? "#f44336"
                : "#9e9e9e",
            color: "white",
          }}
        >
          {selectedPointsCategory === "positive"
            ? "Positive Points"
            : selectedPointsCategory === "negative"
            ? "Negative Points"
            : "Neutral"}{" "}
          Tasks
          <IconButton
            aria-label="close"
            onClick={handleClosePointsTable}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TableContainer sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Task ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Deadline</TableCell>
                  <TableCell>Actual Completion</TableCell>
                  <TableCell align="center">Points Earned</TableCell>
                  <TableCell>Task Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedPointsCategory &&
                  tasksByPoints[selectedPointsCategory].map((task) => (
                    <TableRow key={task.TaskID}>
                      <TableCell>{task.TaskID}</TableCell>
                      <TableCell>{task.Title}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor:
                              task.Status === "Done"
                                ? "rgba(76, 175, 80, 0.1)"
                                : task.Status === "Progress"
                                ? "rgba(33, 150, 243, 0.1)"
                                : task.Status === "Review"
                                ? "rgba(255, 152, 0, 0.1)"
                                : "rgba(158, 158, 158, 0.1)",
                            color:
                              task.Status === "Done"
                                ? "#4caf50"
                                : task.Status === "Progress"
                                ? "#2196f3"
                                : task.Status === "Review"
                                ? "#ff9800"
                                : "#9e9e9e",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                          }}
                        >
                          {task.Status}
                        </Box>
                      </TableCell>
                      <TableCell>{task.Priority}</TableCell>
                      <TableCell>
                        {new Date(task.DueDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {task.ActualDoneDate ? (
                          <Box
                            sx={{
                              display: "inline-block",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              backgroundColor: "rgba(76, 175, 80, 0.1)",
                              color: "#4caf50",
                              fontWeight: 600,
                              fontSize: "0.85rem",
                            }}
                          >
                            {new Date(task.ActualDoneDate).toLocaleDateString()}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "inline-block",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              backgroundColor: "rgba(158, 158, 158, 0.1)",
                              color: "#9e9e9e",
                              fontWeight: 600,
                              fontSize: "0.85rem",
                              fontStyle: "italic",
                            }}
                          >
                            Not Completed
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color:
                              task.Points > 0
                                ? "#4caf50"
                                : task.Points < 0
                                ? "#f44336"
                                : "#9e9e9e",
                          }}
                        >
                          {task.Points > 0 ? "+" : ""}
                          {task.Points}
                        </Typography>
                      </TableCell>
                      <TableCell>{task.TaskType}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Summary at bottom of dialog */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: "rgba(33, 150, 243, 0.05)",
              borderRadius: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Total{" "}
              {selectedPointsCategory === "positive"
                ? "Positive"
                : selectedPointsCategory === "negative"
                ? "Negative"
                : "Neutral"}{" "}
              Tasks:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {selectedPointsCategory &&
                tasksByPoints[selectedPointsCategory].length}
              {selectedPointsCategory !== "neutral" &&
                ` (${
                  selectedPointsCategory === "positive"
                    ? "+" + pointsData.positive
                    : "-" + pointsData.negative
                } points)`}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MyTaskDashboard;
